import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, API_BASE } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { IndivalifeLogo } from '@/components/IndivalifeLogo';
import { Shield, Users, FileText, Settings, AlertTriangle, Trash2, Pencil, Download, Search, CheckCircle2, XCircle, Image as ImageIcon, Package, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { ImageZoom } from '@/components/ImageZoom';
import { UploadField } from '@/components/UploadField';
import { downloadBlob } from '@/lib/download';

const AdminReports = () => {
  const [week, setWeek] = useState('');  // empty = company-wide last-12 view
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (wk) => {
    setLoading(true);
    try {
      const q = wk ? `?week=${encodeURIComponent(wk)}` : '';
      const res = await api.get(`/admin/weekly-statements${q}`);
      setData(res.data);
    } catch (err) {
      toast.error('Could not load report');
      console.error(err);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(week); }, [week]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = 'Personal ID,Name,Phone,Referral,Match,Reward,Gross,TDS (5%),Net,Forfeited,Entries';
    const lines = data.rows.map(r => [
      r.personal_id || '', (r.full_name || '').replace(/,/g, ' '), r.phone || '',
      r.referral_gross, r.match_gross, r.reward_gross ?? 0, r.gross, r.tds, r.net, r.forfeited, r.entries,
    ].join(','));
    const csv = [header, ...lines].join('\n');
    downloadBlob(`indivalife-week-${week || 'rollup'}.csv`, new Blob([csv], { type: 'text/csv' }));
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-bold display text-lg">Weekly payout & TDS report</div>
          <p className="text-xs text-[var(--bz-muted)] mt-1">Per-distributor breakdown of referral + match income, TDS (5%), net payout and forfeited amounts.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Week (YYYY-Www)</Label>
            <Input value={week} onChange={e => setWeek(e.target.value.trim())} placeholder="2026-W26 (blank = last 12 weeks)" data-testid="admin-reports-week-input" className="w-44" />
          </div>
          <Button onClick={exportCsv} variant="outline" data-testid="admin-reports-csv-btn" disabled={!data?.rows?.length}>Export CSV</Button>
        </div>
      </div>
      {loading && <div className="text-sm text-[var(--bz-muted)]">Loading…</div>}
      {!loading && week && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Card className="p-2 text-center"><div className="text-[10px] uppercase text-[var(--bz-muted)]">Distributors</div><div className="text-xl font-black">{data.totals?.distributors || 0}</div></Card>
            <Card className="p-2 text-center"><div className="text-[10px] uppercase text-[var(--bz-muted)]">Gross</div><div className="text-xl font-black">₹{data.totals?.gross || 0}</div></Card>
            <Card className="p-2 text-center"><div className="text-[10px] uppercase text-[var(--bz-muted)]">TDS</div><div className="text-xl font-black text-amber-700">₹{data.totals?.tds || 0}</div></Card>
            <Card className="p-2 text-center"><div className="text-[10px] uppercase text-[var(--bz-muted)]">Net payout</div><div className="text-xl font-black text-[#1D4ED8]">₹{data.totals?.net || 0}</div></Card>
            <Card className="p-2 text-center"><div className="text-[10px] uppercase text-[var(--bz-muted)]">Forfeited</div><div className="text-xl font-black text-red-600">₹{data.totals?.forfeited || 0}</div></Card>
          </div>
          <Card className="p-0 overflow-x-auto bz-scroll">
            <table className="w-full text-sm min-w-[760px]" data-testid="admin-reports-table">
              <thead className="bg-[var(--bz-soft)]"><tr>
                <th className="text-left p-3">Personal ID</th>
                <th className="text-left p-3">Name</th>
                <th className="text-right p-3">Referral</th>
                <th className="text-right p-3">Match</th>
                <th className="text-right p-3">Reward</th>
                <th className="text-right p-3">Gross</th>
                <th className="text-right p-3">TDS</th>
                <th className="text-right p-3">Net</th>
                <th className="text-right p-3">Forfeited</th>
              </tr></thead>
              <tbody>
                {(data.rows || []).length === 0 && <tr><td colSpan={9} className="text-center p-6 text-[var(--bz-muted)]">No payouts for this week.</td></tr>}
                {(data.rows || []).map(r => (
                  <tr key={r.user_id} className="border-t">
                    <td className="p-3 font-mono-bz text-xs">{r.personal_id}</td>
                    <td className="p-3">{r.full_name || '—'}<div className="text-[10px] text-[var(--bz-muted)]">{r.phone}</div></td>
                    <td className="p-3 text-right font-mono-bz">₹{r.referral_gross}</td>
                    <td className="p-3 text-right font-mono-bz">₹{r.match_gross}</td>
                    <td className="p-3 text-right font-mono-bz">₹{r.reward_gross ?? 0}</td>
                    <td className="p-3 text-right font-mono-bz font-bold">₹{r.gross}</td>
                    <td className="p-3 text-right font-mono-bz text-amber-700">₹{r.tds}</td>
                    <td className="p-3 text-right font-mono-bz font-bold text-[#1D4ED8]">₹{r.net}</td>
                    <td className="p-3 text-right font-mono-bz text-red-600">{r.forfeited > 0 ? `₹${r.forfeited}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
      {!loading && !week && data && (
        <Card className="p-0 overflow-x-auto bz-scroll">
          <div className="p-3 text-xs text-[var(--bz-muted)]">Company-wide last-12-weeks rollup. Enter a specific week above (e.g. <code className="bg-[var(--bz-soft)] px-1 rounded">2026-W26</code>) to drill down by distributor.</div>
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[var(--bz-soft)]"><tr>
              <th className="text-left p-3">Week</th>
              <th className="text-right p-3">Gross</th>
              <th className="text-right p-3">TDS</th>
              <th className="text-right p-3">Net payout</th>
              <th className="text-right p-3">Forfeited</th>
              <th className="text-right p-3">Entries</th>
            </tr></thead>
            <tbody>
              {(data.weekly || []).length === 0 && <tr><td colSpan={6} className="text-center p-6 text-[var(--bz-muted)]">No weekly data yet.</td></tr>}
              {(data.weekly || []).map(w => (
                <tr key={w.week_key} className="border-t hover:bg-[var(--bz-soft)] cursor-pointer" onClick={() => setWeek(w.week_key)}>
                  <td className="p-3 font-mono-bz text-xs">{w.week_key}</td>
                  <td className="p-3 text-right font-mono-bz font-bold">₹{w.gross}</td>
                  <td className="p-3 text-right font-mono-bz text-amber-700">₹{w.tds}</td>
                  <td className="p-3 text-right font-mono-bz font-bold text-[#1D4ED8]">₹{w.net}</td>
                  <td className="p-3 text-right font-mono-bz text-red-600">{w.forfeited > 0 ? `₹${w.forfeited}` : '—'}</td>
                  <td className="p-3 text-right font-mono-bz text-[var(--bz-muted)]">{w.entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </Card>
  );
};

const KitFulfilment = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');  // all | processing | shipped | delivered
  const [tracking, setTracking] = useState({});  // { [kit_order_id]: { courier, tracking } }

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/kit-orders');
      setOrders(res.data.orders || []);
    } catch { toast.error('Could not load kit orders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (o, status) => {
    const t = tracking[o.kit_order_id] || {};
    try {
      await api.post(`/admin/kit-orders/${o.kit_order_id}/status`, {
        status,
        courier: t.courier ?? o.courier ?? '',
        tracking: t.tracking ?? o.tracking ?? '',
      });
      toast.success(`Kit marked ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const statusPill = (s) => {
    const map = {
      delivered: 'bg-green-100 text-green-700',
      shipped: 'bg-blue-100 text-blue-700',
      processing: 'bg-amber-100 text-amber-800',
    };
    return <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${map[s] || 'bg-slate-100 text-slate-600'}`}>{(s || 'processing').toUpperCase()}</span>;
  };

  const rows = orders.filter(o => filter === 'all' || (o.fulfilment || 'processing') === filter);
  const counts = orders.reduce((a, o) => { const s = o.fulfilment || 'processing'; a[s] = (a[s] || 0) + 1; return a; }, {});

  return (
    <Card className="p-4 space-y-4" data-testid="admin-kits">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-bold display text-lg flex items-center gap-2"><Package className="h-5 w-5 text-[#1E3A8A]" />Entry Kit fulfilment</div>
          <p className="text-xs text-[var(--bz-muted)] mt-1">Every paid Entry Kit with the buyer's shipping address. Mark each Shipped or Delivered as you dispatch.</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['all', 'processing', 'shipped', 'delivered'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`kit-filter-${f}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${filter === f ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[var(--bz-muted)] border-slate-200'}`}>
              {f[0].toUpperCase() + f.slice(1)}{f !== 'all' && counts[f] ? ` (${counts[f]})` : ''}
            </button>
          ))}
        </div>
      </div>
      {loading && <div className="text-sm text-[var(--bz-muted)]">Loading…</div>}
      {!loading && rows.length === 0 && <Card className="p-6 text-center text-[var(--bz-muted)]" data-testid="kits-empty">No Entry Kit orders {filter !== 'all' ? `with status "${filter}"` : 'yet'}.</Card>}
      <div className="grid gap-3">
        {rows.map(o => {
          const s = o.fulfilment || 'processing';
          return (
            <Card key={o.kit_order_id} className="p-4" data-testid={`kit-order-${o.kit_order_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-[220px]">
                  <div className="flex items-center gap-2">
                    <div className="font-bold">{o.buyer_name || '—'}</div>
                    {statusPill(s)}
                  </div>
                  <div className="text-xs text-[var(--bz-muted)] mt-0.5">{o.buyer_phone || '—'} · ID <span className="font-mono-bz">{o.personal_id || '—'}</span></div>
                  <div className="text-sm mt-2 leading-relaxed">
                    {o.address || '—'}{o.city ? `, ${o.city}` : ''}{o.state ? `, ${o.state}` : ''}
                    {o.pincode ? <span className="font-mono-bz font-semibold"> — {o.pincode}</span> : ''}
                  </div>
                  <div className="text-[11px] text-[var(--bz-muted)] mt-1">Paid ₹{o.amount_inr} · {o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</div>
                </div>
                <div className="flex flex-col gap-2 min-w-[240px]">
                  <div className="grid grid-cols-2 gap-1.5">
                    <Input placeholder="Courier" defaultValue={o.courier || ''} onChange={e => setTracking(t => ({ ...t, [o.kit_order_id]: { ...t[o.kit_order_id], courier: e.target.value } }))} data-testid={`kit-courier-${o.kit_order_id}`} className="h-8 text-xs" />
                    <Input placeholder="Tracking #" defaultValue={o.tracking || ''} onChange={e => setTracking(t => ({ ...t, [o.kit_order_id]: { ...t[o.kit_order_id], tracking: e.target.value } }))} data-testid={`kit-tracking-${o.kit_order_id}`} className="h-8 text-xs" />
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant={s === 'shipped' ? 'default' : 'outline'} onClick={() => setStatus(o, 'shipped')} className={`gap-1 h-8 text-xs flex-1 ${s === 'shipped' ? 'bg-[#1D4ED8]' : ''}`} data-testid={`kit-ship-${o.kit_order_id}`}><Truck className="h-3.5 w-3.5" />Shipped</Button>
                    <Button size="sm" variant={s === 'delivered' ? 'default' : 'outline'} onClick={() => setStatus(o, 'delivered')} className={`gap-1 h-8 text-xs flex-1 ${s === 'delivered' ? 'bg-green-600 hover:bg-green-700' : ''}`} data-testid={`kit-deliver-${o.kit_order_id}`}><CheckCircle2 className="h-3.5 w-3.5" />Delivered</Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </Card>
  );
};


const BLANK_KIT = { kit_id: null, name: '', description: '', images: [], cost: 600, gst_pct: 18, bv: 7, delivery: 60, validity_days: 30, referral_income: 250, cashback: 0, active: true };

const PurchaseKitsAdmin = () => {
  const [kits, setKits] = useState([]);
  const [report, setReport] = useState({ rows: [], total_sold: 0, total_revenue: 0, unassigned: { sold: 0, revenue: 0 } });
  const [editing, setEditing] = useState(null); // kit object being edited/created
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/purchase-kits'); setKits(data.kits || []);
      const r = await api.get('/admin/kit-sales-report'); setReport(r.data || { rows: [] });
    }
    catch { toast.error('Could not load kits'); }
  };
  useEffect(() => { load(); }, []);
  const salesFor = (kid) => (report.rows || []).find(r => r.kit_id === kid) || { sold: 0, revenue: 0 };

  const save = async () => {
    if (!editing.name.trim()) { toast.error('Kit name is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/purchase-kits', {
        kit_id: editing.kit_id || undefined,
        name: editing.name, description: editing.description,
        images: (editing.images || []).filter(Boolean),
        cost: Number(editing.cost) || 0, gst_pct: Number(editing.gst_pct) || 0,
        bv: Number(editing.bv) || 0, delivery: Number(editing.delivery) || 0,
        validity_days: Number(editing.validity_days) || 30,
        referral_income: Number(editing.referral_income) || 0,
        cashback: Number(editing.cashback) || 0,
        active: editing.active !== false,
      });
      toast.success(editing.kit_id ? 'Kit updated' : 'Kit added');
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (kit) => {
    if (!window.confirm(`Remove "${kit.name}"? Buyers will no longer see it.`)) return;
    try { await api.delete(`/admin/purchase-kits/${kit.kit_id}`); toast.success('Kit removed'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const setImg = (idx, url) => setEditing(e => {
    const imgs = [...(e.images || [])];
    if (url) imgs[idx] = url; else imgs.splice(idx, 1);
    return { ...e, images: imgs };
  });

  return (
    <div className="space-y-4" data-testid="purchase-kits-admin">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-bold display text-lg flex items-center gap-2"><Package className="h-5 w-5 text-[#1E3A8A]" />Purchase Kits</div>
          <p className="text-xs text-[var(--bz-muted)] mt-1">Create multiple kits — each with its own name, price, GST, BV, validity, delivery, sponsor referral & images. These show on the Network join screen.</p>
        </div>
        <Button onClick={() => setEditing({ ...BLANK_KIT })} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="add-purchase-kit-btn">+ Add kit</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="kit-sales-summary">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold">Total kits sold</div><div className="text-2xl font-black display text-[#1E3A8A]">{report.total_sold || 0}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold">Total kit revenue</div><div className="text-2xl font-black display text-[#1E3A8A]">₹{Math.round(report.total_revenue || 0).toLocaleString('en-IN')}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold">Active kits</div><div className="text-2xl font-black display text-[#1E3A8A]">{kits.filter(k => k.active).length}</div></Card>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {kits.map(k => {
          const s = salesFor(k.kit_id);
          return (
          <Card key={k.kit_id} className={`p-3 flex gap-3 ${k.active ? '' : 'opacity-60'}`} data-testid={`kit-row-${k.kit_id}`}>
            {k.image_url ? <img src={k.image_url} alt={k.name} className="h-16 w-16 rounded-md object-cover border shrink-0" /> : <div className="h-16 w-16 rounded-md bg-[var(--bz-soft)] grid place-items-center shrink-0">📦</div>}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#1E3A8A] truncate">{k.name} {!k.active && <Badge variant="destructive" className="ml-1">hidden</Badge>}</div>
              <div className="text-xs text-[var(--bz-muted)]">₹{Math.round(k.total)} total · {k.validity_days}d · BV {k.bv} · ref ₹{k.referral_income}{k.cashback ? ` · cashback ₹${k.cashback}` : ''}</div>
              <div className="text-[11px] text-[var(--bz-muted)]">₹{k.cost} + {k.gst_pct}% GST + ₹{k.delivery} delivery</div>
              <div className="mt-1.5 inline-flex items-center gap-3 rounded-md bg-[var(--bz-soft)] px-2 py-1 text-[11px] font-semibold text-[#1E3A8A]" data-testid={`kit-sales-${k.kit_id}`}>
                <span>Sold: {s.sold}</span><span>Revenue: ₹{Math.round(s.revenue).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...BLANK_KIT, ...k })} data-testid={`edit-kit-${k.kit_id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => remove(k)} data-testid={`delete-kit-${k.kit_id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>
              </div>
            </div>
          </Card>
          );
        })}
        {kits.length === 0 && <Card className="p-6 text-center text-[var(--bz-muted)]" data-testid="kits-list-empty">No kits yet. Tap "Add kit" to create one.</Card>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="kit-editor">
          <DialogHeader><DialogTitle>{editing?.kit_id ? 'Edit' : 'Add'} Purchase Kit</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Kit name *</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Starter Kit" data-testid="kit-name-input" /></div>
              <div><Label>Description</Label><Textarea value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} rows={2} data-testid="kit-desc-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Price ₹ (excl. GST/delivery)</Label><Input type="number" value={editing.cost} onChange={e => setEditing({ ...editing, cost: e.target.value })} data-testid="kit-cost-input" /></div>
                <div><Label>GST %</Label><Input type="number" value={editing.gst_pct} onChange={e => setEditing({ ...editing, gst_pct: e.target.value })} data-testid="kit-gst-input" /></div>
                <div><Label>BV</Label><Input type="number" value={editing.bv} onChange={e => setEditing({ ...editing, bv: e.target.value })} data-testid="kit-bv-input" /></div>
                <div><Label>Delivery ₹</Label><Input type="number" value={editing.delivery} onChange={e => setEditing({ ...editing, delivery: e.target.value })} data-testid="kit-delivery-input" /></div>
                <div><Label>Validity (days)</Label><Input type="number" value={editing.validity_days} onChange={e => setEditing({ ...editing, validity_days: e.target.value })} data-testid="kit-validity-input" /></div>
                <div><Label>Sponsor referral ₹</Label><Input type="number" value={editing.referral_income} onChange={e => setEditing({ ...editing, referral_income: e.target.value })} data-testid="kit-referral-input" /></div>
                <div><Label>Buyer cashback ₹</Label><Input type="number" value={editing.cashback} onChange={e => setEditing({ ...editing, cashback: e.target.value })} data-testid="kit-cashback-input" /></div>
              </div>
              <div>
                <Label>Kit images</Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {(editing.images || []).map((url, idx) => (
                    <UploadField key={`kimg-${idx}`} value={url} onChange={(u) => setImg(idx, u)} testId={`kit-img-${idx}`} />
                  ))}
                  <UploadField key={`kimg-add-${(editing.images || []).length}`} value={null} onChange={(u) => u && setImg((editing.images || []).length, u)} testId="kit-img-add" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="kit-active-toggle" />
                Visible to buyers (active)
              </label>
              <div className="rounded-md bg-[var(--bz-soft)] p-2 text-sm font-semibold text-[#1E3A8A]">
                Total payable: ₹{Math.round((Number(editing.cost) || 0) * (1 + (Number(editing.gst_pct) || 0) / 100) + (Number(editing.delivery) || 0))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="save-kit-btn">{saving ? 'Saving…' : 'Save kit'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};



const RewardLog = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/reward-log').then(r => setRows(r.data?.rows || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <Card className="p-4" data-testid="reward-log-card">
      <div className="font-bold display mb-3">Weekly PV reward claims ({rows.length})</div>
      {loading ? <div className="text-sm text-[var(--bz-muted)]">Loading…</div> : rows.length === 0 ? (
        <div className="text-sm text-[var(--bz-muted)]" data-testid="reward-log-empty">No reward claims yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] text-left">
                <th className="py-1.5 pr-3">Member</th>
                <th className="py-1.5 pr-3">ID</th>
                <th className="py-1.5 pr-3">Phone</th>
                <th className="py-1.5 pr-3">Claimed on</th>
                <th className="py-1.5 pr-3">PV</th>
                <th className="py-1.5 pr-3">Items</th>
                <th className="py-1.5">Total</th>
              </tr>
            </thead>
            <tbody data-testid="reward-log-body">
              {rows.map((r, i) => (
                <tr key={r.order_id || i} className="border-t border-[var(--bz-line)]" data-testid={`reward-log-row-${i}`}>
                  <td className="py-1.5 pr-3 font-semibold">{r.full_name || '—'}</td>
                  <td className="py-1.5 pr-3">{r.personal_id || '—'}</td>
                  <td className="py-1.5 pr-3">{r.phone || '—'}</td>
                  <td className="py-1.5 pr-3">{(r.redeemed_at || '').slice(0, 10)}</td>
                  <td className="py-1.5 pr-3">{r.pv ?? '—'}</td>
                  <td className="py-1.5 pr-3">{r.items ?? '—'}</td>
                  <td className="py-1.5 font-semibold">₹{r.total ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const WithdrawalsAdmin = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = async (f) => {
    setLoading(true);
    try {
      const q = f && f !== 'all' ? `?status=${f}` : '';
      const { data } = await api.get(`/admin/withdrawals${q}`);
      setRows(data?.rows || []);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (id, approve) => {
    if (approve && !window.confirm('Mark this withdrawal as PAID? The member wallet will reduce by this amount.')) return;
    const fd = new FormData();
    fd.append('approve', approve ? 'true' : 'false');
    try {
      await api.post(`/admin/withdrawals/${id}/decide`, fd);
      toast.success(approve ? 'Marked paid' : 'Rejected');
      load(filter);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const generate = async () => {
    try {
      const { data } = await api.post('/admin/withdrawals/generate');
      toast.success(`${data.created} new batch(es) generated`);
      load(filter);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="withdrawals-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 text-xs">
          {['pending', 'paid', 'rejected', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`wd-filter-${f}`}
              className={`px-3 py-1 rounded-full font-semibold capitalize ${filter === f ? 'bg-[#1D4ED8] text-white' : 'bg-[var(--bz-soft)]'}`}>{f}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={generate} variant="outline" size="sm" data-testid="wd-generate-btn">Generate batches</Button>
          <Button onClick={() => { const q = filter && filter !== 'all' ? `?status=${filter}` : ''; window.open(`${API_BASE}/admin/withdrawals/export.csv${q}`, '_blank'); }} variant="outline" size="sm" data-testid="wd-export-btn">Export CSV</Button>
        </div>
      </div>
      {loading ? <div className="text-sm text-[var(--bz-muted)]">Loading…</div> : rows.length === 0 ? (
        <div className="text-sm text-[var(--bz-muted)]" data-testid="withdrawals-empty">No withdrawals in this list.</div>
      ) : (
        <div className="grid gap-2">
          {rows.map((w) => (
            <Card key={w.withdrawal_id} className="p-4" data-testid={`withdrawal-${w.withdrawal_id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="font-bold">{w.member_name || w.personal_id} <span className="text-xs text-[var(--bz-muted)]">· {w.personal_id}</span></div>
                  <div className="text-[11px] text-[var(--bz-muted)]">Week {w.week_key} · {w.member_phone || '—'} · created {(w.created_at || '').slice(0, 10)}</div>
                  <div className="mt-2 text-xs grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                    <div><span className="text-[var(--bz-muted)]">A/C holder:</span> <b>{w.bank?.account_holder || '—'}</b></div>
                    <div><span className="text-[var(--bz-muted)]">A/C no:</span> <b>{w.bank?.bank_account || '—'}</b></div>
                    <div><span className="text-[var(--bz-muted)]">IFSC:</span> <b>{w.bank?.ifsc || '—'}</b></div>
                    <div><span className="text-[var(--bz-muted)]">PAN:</span> <b>{w.bank?.pan || '—'}</b></div>
                    <div><span className="text-[var(--bz-muted)]">Aadhaar:</span> <b>{w.bank?.aadhaar || '—'}</b></div>
                    {w.bank?.bankbook_image_url && <a href={w.bank.bankbook_image_url} target="_blank" rel="noreferrer" className="text-[#1D4ED8] font-semibold">View bank proof ↗</a>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-black display text-green-700">₹{w.amount}</div>
                  <div className="text-[10px] text-[var(--bz-muted)]">gross ₹{w.gross} · TDS ₹{w.tds}</div>
                  <Badge className={`mt-1 ${w.status === 'paid' ? 'bg-green-600' : w.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'}`}>{w.status}</Badge>
                  {w.status === 'pending' && (
                    <div className="flex gap-2 mt-2 justify-end">
                      <Button size="sm" onClick={() => decide(w.withdrawal_id, true)} className="bg-green-600 hover:bg-green-700" data-testid={`wd-pay-${w.withdrawal_id}`}>Mark paid</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(w.withdrawal_id, false)} data-testid={`wd-reject-${w.withdrawal_id}`}>Reject</Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


const CRITERIA_KINDS = [
  { value: 'base', label: 'Base / starting rank (no criteria)' },
  { value: 'bv_window', label: 'Personal BV in a window' },
  { value: 'downline_rank', label: 'Downline of a rank per leg' },
  { value: 'earnings', label: 'Total earnings ≥' },
  { value: 'earnings_legbv', label: 'Earnings + leg BV (sustained)' },
];

const RankRow = ({ rank, allRanks, onSaved, onDelete }) => {
  const [r, setR] = useState(rank);
  const [busy, setBusy] = useState(false);
  const c = r.criteria || {};
  const setC = (patch) => setR({ ...r, criteria: { ...c, ...patch } });

  const save = async () => {
    setBusy(true);
    try {
      await api.post('/admin/ranks', {
        rank_id: r.rank_id, slug: r.slug, name: r.name, level: Number(r.level) || 0,
        badge_url: r.badge_url || '', desc: r.desc || '', criteria: r.criteria || {}, active: r.active !== false,
      });
      toast.success(`${r.name} saved`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4" data-testid={`rank-row-${r.slug}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex flex-col items-center gap-1">
          {r.badge_url ? <img src={r.badge_url} alt="" className="h-16 w-16 object-contain rounded-lg border" data-testid={`rank-badge-preview-${r.slug}`} /> : <div className="h-16 w-16 grid place-items-center rounded-lg border bg-[var(--bz-soft)] text-[9px] text-[var(--bz-muted)]">No badge</div>}
          <UploadField value={r.badge_url || null} onChange={(u) => setR({ ...r, badge_url: u || '' })} testId={`rank-badge-${r.slug}`} />
        </div>
        <div className="flex-1 min-w-[240px] grid sm:grid-cols-2 gap-2">
          <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Level</Label><Input type="number" value={r.level} onChange={(e) => setR({ ...r, level: e.target.value })} data-testid={`rank-level-${r.slug}`} /></div>
          <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Name</Label><Input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} data-testid={`rank-name-${r.slug}`} /></div>
          <div className="sm:col-span-2"><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Criteria type</Label>
            <select value={c.kind || 'bv_window'} onChange={(e) => setC({ kind: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm" data-testid={`rank-kind-${r.slug}`}>
              {CRITERIA_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          {c.kind === 'bv_window' && (<>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">BV target</Label><Input type="number" value={c.bv ?? ''} onChange={(e) => setC({ bv: Number(e.target.value) })} data-testid={`rank-bv-${r.slug}`} /></div>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Window (days)</Label><Input type="number" value={c.window_days ?? ''} onChange={(e) => setC({ window_days: Number(e.target.value) })} data-testid={`rank-window-${r.slug}`} /></div>
          </>)}
          {c.kind === 'downline_rank' && (<>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Of rank</Label>
              <select value={c.of_slug || ''} onChange={(e) => setC({ of_slug: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm" data-testid={`rank-ofslug-${r.slug}`}>
                <option value="">— select —</option>
                {allRanks.filter(x => x.slug !== r.slug).map(x => <option key={x.slug} value={x.slug}>{x.name}</option>)}
              </select>
            </div>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Per leg</Label><Input type="number" value={c.per_leg ?? ''} onChange={(e) => setC({ per_leg: Number(e.target.value) })} data-testid={`rank-perleg-${r.slug}`} /></div>
            <label className="sm:col-span-2 flex items-center gap-2 text-xs font-semibold" data-testid={`rank-personally-${r.slug}`}>
              <input type="checkbox" checked={!!c.personally_sponsored} onChange={(e) => setC({ personally_sponsored: e.target.checked })} />
              Must be personally sponsored (direct) on each leg
            </label>
          </>)}
          {c.kind === 'earnings' && (
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Earnings ≥ ₹</Label><Input type="number" value={c.earnings ?? ''} onChange={(e) => setC({ earnings: Number(e.target.value) })} data-testid={`rank-earnings-${r.slug}`} /></div>
          )}
          {c.kind === 'earnings_legbv' && (<>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Earnings ≥ ₹</Label><Input type="number" value={c.earnings ?? ''} onChange={(e) => setC({ earnings: Number(e.target.value) })} data-testid={`rank-earnings-${r.slug}`} /></div>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">BV each leg</Label><Input type="number" value={c.leg_bv ?? ''} onChange={(e) => setC({ leg_bv: Number(e.target.value) })} data-testid={`rank-legbv-${r.slug}`} /></div>
            <div><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Sustain (weeks)</Label><Input type="number" value={c.sustain_weeks ?? ''} onChange={(e) => setC({ sustain_weeks: Number(e.target.value) })} data-testid={`rank-sustain-${r.slug}`} /></div>
          </>)}
          <div className="sm:col-span-2"><Label className="text-[10px] uppercase text-[var(--bz-muted)]">Description</Label><Input value={r.desc || ''} onChange={(e) => setR({ ...r, desc: e.target.value })} data-testid={`rank-desc-${r.slug}`} /></div>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="sm" onClick={save} disabled={busy} className="bg-[#1D4ED8]" data-testid={`rank-save-${r.slug}`}>{busy ? 'Saving…' : 'Save'}</Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(r.rank_id)} data-testid={`rank-delete-${r.slug}`}>Delete</Button>
        </div>
      </div>
    </Card>
  );
};

const RanksAdmin = () => {
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/ranks'); setRanks(data?.ranks || []); }
    catch { toast.error('Load failed'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const addRank = () => {
    const nextLevel = (ranks.reduce((m, r) => Math.max(m, r.level || 0), 0)) + 1;
    setRanks([...ranks, { rank_id: null, slug: `rank_${nextLevel}`, name: `Rank ${nextLevel}`, level: nextLevel, badge_url: '', desc: '', criteria: { kind: 'earnings', earnings: 0 }, active: true }]);
  };
  const del = async (id) => {
    if (!id) { load(); return; }
    if (!window.confirm('Delete this rank?')) return;
    try { await api.delete(`/admin/ranks/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3" data-testid="ranks-admin">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[var(--bz-muted)]">Manage the rank ladder — upload a badge, set thresholds and order. Ranks are permanent once earned by a member.</div>
        <Button size="sm" onClick={addRank} data-testid="rank-add-btn">＋ Add rank</Button>
      </div>
      {loading ? <div className="text-sm text-[var(--bz-muted)]">Loading…</div> : ranks.map(r => (
        <RankRow key={r.rank_id || r.slug} rank={r} allRanks={ranks} onSaved={load} onDelete={del} />
      ))}
    </div>
  );
};


const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { secret } = useParams();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [pendingKind, setPendingKind] = useState('network_signups');
  const [pending, setPending] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ date_from: '', date_to: '', action: '' });
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editJson, setEditJson] = useState('{}');
  const [editReason, setEditReason] = useState('');
  const [eraseOpen, setEraseOpen] = useState(false);
  const [eraseSubtree, setEraseSubtree] = useState('');
  const [eraseReason, setEraseReason] = useState('');
  const [userAudit, setUserAudit] = useState({});
  const [counts, setCounts] = useState({ approvals: 0, ekyc: 0, total: 0 });

  useEffect(() => {
    if (!user) { navigate('/admin/login'); return; }
    if (!user.is_admin) { toast.error('Admin only'); navigate('/'); return; }
    loadStats();
    loadCounts();
    const id = setInterval(loadCounts, 30000);
    return () => clearInterval(id);
  }, [user]); // eslint-disable-line

  const loadCounts = async () => {
    try { const { data } = await api.get('/admin/pending-counts'); setCounts(data); } catch { /* noop */ }
  };

  const loadStats = async () => {
    const { data } = await api.get('/admin/stats'); setStats(data);
  };
  const loadUsers = async () => {
    const { data } = await api.get('/admin/users', { params: { q: search } }); setUsers(data);
  };
  const loadPending = async (kind = pendingKind) => {
    const { data } = await api.get('/admin/pending', { params: { kind } }); setPending(data); setPendingKind(kind);
  };
  const loadAudit = async () => {
    const params = {};
    if (auditFilter.date_from) params.date_from = auditFilter.date_from;
    if (auditFilter.date_to) params.date_to = auditFilter.date_to;
    if (auditFilter.action) params.action = auditFilter.action;
    const { data } = await api.get('/admin/audit', { params }); setAuditLog(data);
  };

  const exportAuditCsv = async () => {
    try {
      const params = {};
      if (auditFilter.date_from) params.date_from = auditFilter.date_from;
      if (auditFilter.date_to) params.date_to = auditFilter.date_to;
      if (auditFilter.action) params.action = auditFilter.action;
      await downloadBlob({ path: '/admin/audit.csv', method: 'GET', params, filename: `audit_log_${new Date().toISOString().slice(0, 10)}.csv` });
      toast.success('CSV downloaded');
    } catch (e) { toast.error(`Export failed: ${e.message}`); }
  };
  const loadSettings = async () => {
    const { data } = await api.get('/admin/settings'); setSettings(data);
  };

  const approve = async (req, ok = true) => {
    const fd = new FormData(); fd.append('kind', pendingKind); fd.append('request_id', req.signup_id || req.renewal_id || req.subscription_id || req.withdrawal_id); fd.append('approve', ok);
    try {
      await api.post('/admin/approve', fd);
      toast.success(ok ? 'Approved' : 'Rejected'); loadPending(); loadStats(); loadCounts();
    } catch { toast.error('Failed'); }
  };

  const openEdit = async (u) => {
    setEditingUser(u);
    setEditJson(JSON.stringify({ name: u.name, nickname: u.nickname, phone: u.phone, town: u.town, pincode: u.pincode, is_member: u.is_member }, null, 2));
    setEditReason(''); setEditOpen(true);
    const { data } = await api.get(`/admin/audit/user/${u.user_id}`); setUserAudit({ ...userAudit, [u.user_id]: data });
  };
  const saveEdit = async () => {
    try {
      const updates = JSON.parse(editJson);
      await api.post('/admin/users/edit', { target_user_id: editingUser.user_id, updates, reason: editReason });
      toast.success('User updated'); setEditOpen(false); loadUsers(); loadAudit();
    } catch (e) { toast.error('Invalid JSON or save failed'); }
  };

  const erase = async () => {
    try {
      await api.post('/admin/network/erase', { subtree_personal_id: eraseSubtree || null, reason: eraseReason });
      toast.success('Erased'); setEraseOpen(false); setEraseSubtree(''); setEraseReason(''); loadStats(); loadAudit();
    } catch { toast.error('Failed'); }
  };

  const updateSetting = async (k, v) => {
    try {
      await api.post('/admin/settings', { key: k, value: v }); toast.success('Saved'); loadSettings();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-[var(--bz-soft)]">
      <header className="bg-white border-b border-[var(--bz-line)] sticky top-0 z-10">
        <div className="max-w-screen-2xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IndivalifeLogo size={32} />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--bz-muted)] font-semibold">INDIVALIFE</div>
              <div className="text-lg font-black tracking-tighter display flex items-center gap-2"><Shield className="h-4 w-4 text-[#1E3A8A]" />Admin Control Room</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{user?.email}</Badge>
            <Button variant="outline" size="sm" onClick={() => navigate('/chats')} data-testid="admin-back-app">Back to app</Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout">Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto p-6">
        {counts.total > 0 && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3" data-testid="admin-pending-alert">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="text-sm text-amber-900">
              <b>{counts.total}</b> item{counts.total > 1 ? 's' : ''} need{counts.total > 1 ? '' : 's'} your attention —
              {counts.approvals > 0 && <> <b>{counts.approvals}</b> pending approval{counts.approvals > 1 ? 's' : ''}</>}
              {counts.approvals > 0 && counts.ekyc > 0 && ' ·'}
              {counts.ekyc > 0 && <> <b>{counts.ekyc}</b> eKYC to verify</>}.
            </div>
          </div>
        )}
        {/* Stats */}
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            ['Users', stats.users], ['Members', stats.members],
            ['Signups', stats.pending_signups], ['Renewals', stats.pending_renewals],
            ['Withdrawals', stats.pending_withdrawals],
          ].map(([label, val]) => (
            <Card key={label} className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">{label}</div>
              <div className="text-3xl font-black display">{val ?? 0}</div>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="pending" onValueChange={(v) => {
          if (v === 'users') loadUsers();
          if (v === 'audit') loadAudit();
          if (v === 'settings') loadSettings();
          if (v === 'pricing') loadSettings();
          if (v === 'pending') { loadPending(); loadCounts(); }
          if (v === 'ekyc') loadCounts();
        }}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="admin-tab-pending">
              Approvals
              {counts.approvals > 0 && <span data-testid="badge-approvals" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold">{counts.approvals}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
            <TabsTrigger value="network" data-testid="admin-tab-network">Network ops</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="admin-tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="prizes" data-testid="admin-tab-prizes">Prizes</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="reward-log" data-testid="admin-tab-reward-log">Reward Log</TabsTrigger>
            <TabsTrigger value="ranks" data-testid="admin-tab-ranks">Ranks</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals">
              Withdrawals
              {counts.withdrawals > 0 && <span data-testid="badge-withdrawals" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">{counts.withdrawals}</span>}
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="kits" data-testid="admin-tab-kits">Kits</TabsTrigger>
            <TabsTrigger value="purchase-kits" data-testid="admin-tab-purchase-kits">Purchase Kits</TabsTrigger>
            <TabsTrigger value="ekyc" data-testid="admin-tab-ekyc">
              eKYC
              {counts.ekyc > 0 && <span data-testid="badge-ekyc" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">{counts.ekyc}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <Card className="p-4">
              <div className="flex gap-2 mb-3 flex-wrap">
                {['network_signups', 'renewals', 'entrepreneur', 'withdrawals'].map(k => (
                  <Button key={k} size="sm" variant={pendingKind === k ? 'default' : 'outline'} onClick={() => loadPending(k)} className={pendingKind === k ? 'bg-[#1D4ED8]' : ''} data-testid={`pending-tab-${k}`}>
                    {k.replace('_', ' ')}
                  </Button>
                ))}
              </div>
              {pending.length === 0 && <div className="p-6 text-sm text-[var(--bz-muted)]">No pending {pendingKind}</div>}
              <div className="space-y-2">
                {pending.map((p, i) => {
                  const shotUrl = p.payment_screenshot_url || p.screenshot_url;
                  const stripped = { ...p };
                  delete stripped.payment_screenshot_url;
                  delete stripped.screenshot_url;
                  return (
                    <div key={i} className="border rounded-md p-3 flex flex-col sm:flex-row items-start gap-3" data-testid={`pending-item-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-bz text-[11px] whitespace-pre-wrap max-h-44 overflow-y-auto bz-scroll bg-[var(--bz-soft)] rounded-md p-2">{JSON.stringify(stripped, null, 2)}</div>
                        <div className="text-[10px] text-[var(--bz-muted)] mt-1">UTR: <b className="font-mono-bz">{p.utr || '—'}</b> · ₹{p.amount || p.payment_amount || '—'}</div>
                      </div>
                      {shotUrl ? (
                        <ImageZoom src={shotUrl} testId={`pending-shot-${i}`}>
                          <img src={shotUrl} alt="payment screenshot" className="h-36 w-36 object-cover rounded-md border-2 border-[#1D4ED8]/40 cursor-zoom-in hover:scale-105 transition-transform" data-testid={`pending-shot-img-${i}`} />
                        </ImageZoom>
                      ) : (
                        <div className="h-36 w-36 rounded-md bg-[var(--bz-soft)] grid place-items-center text-[var(--bz-muted)] text-xs"><ImageIcon className="h-6 w-6" /></div>
                      )}
                      <div className="flex sm:flex-col gap-1">
                        <Button size="sm" onClick={() => approve(p, true)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid={`approve-${i}`}><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => approve(p, false)} data-testid={`reject-${i}`}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <Card className="p-4">
              <div className="flex gap-2 mb-3">
                <Input placeholder="Search by name, email, personal_id…" value={search} onChange={e => setSearch(e.target.value)} data-testid="admin-user-search" />
                <Button onClick={loadUsers} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2"><Search className="h-4 w-4" />Search</Button>
              </div>
              <div className="overflow-x-auto bz-scroll">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bz-soft)]"><tr><th className="text-left p-2">User</th><th className="text-left p-2">Personal ID</th><th className="text-left p-2">Town</th><th className="text-left p-2">Role</th><th className="text-right p-2">Actions</th></tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} className="border-t">
                        <td className="p-2"><div className="font-semibold">{u.name}</div><div className="text-[10px] text-[var(--bz-muted)]">{u.email}</div></td>
                        <td className="p-2 font-mono-bz">{u.personal_id || '—'}</td>
                        <td className="p-2">{u.town || '—'}</td>
                        <td className="p-2">{u.is_admin ? <Badge>Admin</Badge> : u.is_member ? <Badge className="bg-[#1D4ED8]">Member</Badge> : <Badge variant="outline">User</Badge>}</td>
                        <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}><Pencil className="h-3 w-3" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <Card className="p-4">
              <div className="flex justify-between items-start mb-3 gap-3 flex-wrap">
                <div className="text-sm font-semibold">Audit log ({auditLog.length})</div>
                <Button variant="outline" size="sm" onClick={exportAuditCsv} data-testid="export-audit-btn">
                  <Download className="h-4 w-4 mr-1" />Export CSV
                </Button>
              </div>
              <div className="grid sm:grid-cols-4 gap-2 mb-3 items-end" data-testid="audit-filter-row">
                <div>
                  <Label className="text-xs">From (ISO date)</Label>
                  <Input type="date" value={auditFilter.date_from} onChange={e => setAuditFilter(f => ({ ...f, date_from: e.target.value }))} data-testid="audit-from" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={auditFilter.date_to} onChange={e => setAuditFilter(f => ({ ...f, date_to: e.target.value }))} data-testid="audit-to" />
                </div>
                <div>
                  <Label className="text-xs">Action</Label>
                  <Input placeholder="e.g. approve_signup" value={auditFilter.action} onChange={e => setAuditFilter(f => ({ ...f, action: e.target.value }))} data-testid="audit-action" />
                </div>
                <Button variant="outline" size="sm" onClick={loadAudit} data-testid="audit-apply"><Search className="h-4 w-4 mr-1" />Apply</Button>
              </div>
              <div className="overflow-x-auto bz-scroll">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--bz-soft)]"><tr><th className="text-left p-2">When</th><th className="text-left p-2">Action</th><th className="text-left p-2">Actor</th><th className="text-left p-2">Target</th><th className="text-left p-2">Meta</th></tr></thead>
                  <tbody>
                    {auditLog.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-[var(--bz-muted)] py-6">No audit entries match these filters.</td></tr>
                    )}
                    {auditLog.map(a => (
                      <tr key={a.audit_id} className="border-t">
                        <td className="p-2 font-mono-bz">{new Date(a.at).toLocaleString()}</td>
                        <td className="p-2"><Badge variant="outline">{a.action}</Badge></td>
                        <td className="p-2 font-mono-bz">{a.actor_id?.slice(0, 12)}</td>
                        <td className="p-2 font-mono-bz">{a.target?.slice(0, 18)}</td>
                        <td className="p-2 max-w-xs truncate">{JSON.stringify(a.meta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="network" className="mt-4">
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-yellow-600" /><div className="font-bold display">Destructive network operations</div></div>
              <p className="text-sm text-[var(--bz-muted)] mb-4">Use the erase tools below to clean up a sub-tree under any Personal ID, or wipe the entire genealogy (keeping the company-top BT000001). All actions are recorded in the audit log.</p>
              <Button variant="destructive" onClick={() => setEraseOpen(true)} className="gap-2" data-testid="erase-btn"><Trash2 className="h-4 w-4" />Erase network…</Button>
            </Card>
          </TabsContent>

          {/* Pricing — admin-editable Yearly + Monthly + GST + BV */}
          <TabsContent value="pricing" className="mt-4">
            <PricingPanel settings={settings} updateSetting={updateSetting} />
          </TabsContent>

          {/* Announcements — admin posts updates that show on user dashboards */}
          <TabsContent value="announcements" className="mt-4">
            <AnnouncementsPanel />
          </TabsContent>

          {/* Messages — admin DM to one user or broadcast to all */}
          <TabsContent value="messages" className="mt-4">
            <MessagesPanel />
          </TabsContent>

          <TabsContent value="prizes" className="mt-4">
            <PrizesPanel />
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <Card className="p-4">
              <div className="font-bold display mb-3">Dynamic settings</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(settings).map(([k, v]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-wider">{k.replaceAll('_', ' ')}</Label>
                    <div className="flex gap-2">
                      <Input defaultValue={v} onBlur={(e) => {
                        let parsed = e.target.value;
                        if (!isNaN(Number(parsed)) && typeof v === 'number') parsed = Number(parsed);
                        updateSetting(k, parsed);
                      }} data-testid={`setting-${k}`} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Payments tab — verify live Razorpay with a ₹1 test */}
          <TabsContent value="payments" className="mt-4">
            <Card className="p-4 space-y-3">
              <div>
                <div className="font-bold display">Live Razorpay sanity check</div>
                <p className="text-xs text-[var(--bz-muted)] mt-1">
                  Pay ₹1 to confirm the live key + secret + signature verification work end-to-end. After Razorpay deducts ₹1, you'll see a green "verified" toast and the order shows up in your Razorpay dashboard.
                </p>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const { startRazorpayCheckout } = await import('@/lib/razorpay');
                    const result = await startRazorpayCheckout({
                      purpose: 'admin_test',
                      amount: 1,
                      phone: '9999999999',
                      prefill: { name: 'Admin Test', email: 'admin@indivalife.com', contact: '9999999999' },
                      onDismiss: () => toast('Cancelled'),
                    });
                    if (result?.status === 'paid') {
                      toast.success('Live Razorpay verified successfully');
                    } else if (!result?.dismissed) {
                      toast.success('Razorpay flow completed');
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || e?.description || e?.message || 'Test payment failed');
                  }
                }}
                className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2 h-12 font-bold display"
                data-testid="admin-rzp-test-btn"
              >
                Pay ₹1 to verify live Razorpay
              </Button>
              <p className="text-[10px] text-[var(--bz-muted)]">
                Currently using key: <code className="bg-[var(--bz-soft)] px-1 rounded">{(process.env.REACT_APP_RAZORPAY_KEY_ID || 'unset')}</code>
              </p>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <AdminReports />
          </TabsContent>

          <TabsContent value="reward-log" className="mt-4">
            <RewardLog />
          </TabsContent>

          <TabsContent value="ranks" className="mt-4">
            <RanksAdmin />
          </TabsContent>

          <TabsContent value="withdrawals" className="mt-4">
            <WithdrawalsAdmin />
          </TabsContent>

          <TabsContent value="ekyc" className="mt-4">
            <AdminEkyc onDecision={loadCounts} />
          </TabsContent>

          <TabsContent value="products" className="mt-4">
            <AdminStore />
          </TabsContent>

          <TabsContent value="kits" className="mt-4">
            <KitFulfilment />
          </TabsContent>

          <TabsContent value="purchase-kits" className="mt-4">
            <PurchaseKitsAdmin />
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit user */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit user: {editingUser?.name}</DialogTitle></DialogHeader>
          <Label>Updates (JSON)</Label>
          <Textarea rows={8} value={editJson} onChange={e => setEditJson(e.target.value)} className="font-mono-bz text-xs" data-testid="edit-user-json" />
          <Label>Reason (audit trail)</Label>
          <Input value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="vmail compliance #1234" data-testid="edit-user-reason" />
          {editingUser && (userAudit[editingUser.user_id] || []).length > 0 && (
            <div className="mt-2 max-h-32 overflow-y-auto bz-scroll text-xs">
              <div className="font-bold mb-1">Audit timeline for this user</div>
              {(userAudit[editingUser.user_id] || []).map(a => (
                <div key={a.audit_id} className="border-l-2 border-[#1D4ED8] pl-2 mb-1">
                  <div className="font-semibold">{a.action}</div>
                  <div className="text-[10px] text-[var(--bz-muted)]">{new Date(a.at).toLocaleString()} — {JSON.stringify(a.meta)}</div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={saveEdit} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="save-user-edit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Erase */}
      <Dialog open={eraseOpen} onOpenChange={setEraseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Erase network</DialogTitle></DialogHeader>
          <p className="text-sm text-[var(--bz-muted)]">Leave subtree blank to erase EVERYTHING (except BT000001).</p>
          <div className="space-y-3">
            <div><Label>Subtree Personal ID (optional)</Label><Input value={eraseSubtree} onChange={e => setEraseSubtree(e.target.value)} placeholder="e.g., BT000123" data-testid="erase-subtree" /></div>
            <div><Label>Reason</Label><Input value={eraseReason} onChange={e => setEraseReason(e.target.value)} data-testid="erase-reason" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEraseOpen(false)}>Cancel</Button><Button variant="destructive" onClick={erase} data-testid="confirm-erase">Erase</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;

// ---------- Admin sub-panels ----------

const emptyProduct = { title: '', description: '', category: 'General', hsn_code: '', mrp: 0, offer_price: 0, images: [], sizes: [], colours: [], bv: 0, cashback: 0, weight_kg: 0.5, stock_count: 0, active: true };

const AdminStore = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or null
  const [form, setForm] = useState(emptyProduct);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/admin/store/products'); setRows(res.data.products || []); }
    catch { toast.error('Load failed'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyProduct); setEditing('new'); };
  const openEdit = (p) => { setForm({ ...emptyProduct, ...p }); setEditing(p.product_id); };

  const addImg = (url) => setForm(f => ({ ...f, images: [...(f.images || []), url] }));
  const removeImg = (idx) => setForm(f => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));
  const [imgBusy, setImgBusy] = useState(false);
  const uploadMultiImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgBusy(true);
    try {
      const { uploadImage } = await import('@/lib/upload');
      const { compressImage } = await import('@/lib/compress');
      const compressed = await Promise.all(files.map(f => compressImage(f, { targetKB: 60 })));
      const results = await Promise.all(compressed.map(f => uploadImage(f, { targetKb: 60 })));
      const urls = results.filter(Boolean).map(r => r.url);
      if (urls.length) {
        setForm(f => ({ ...f, images: [...(f.images || []), ...urls] }));
        toast.success(`${urls.length} image${urls.length === 1 ? '' : 's'} added`);
      }
    } catch { toast.error('Image upload failed'); }
    finally { setImgBusy(false); e.target.value = ''; }
  };

  const save = async () => {
    if (!form.title || !form.offer_price) { toast.error('Title and offer price required'); return; }
    setBusy(true);
    const payload = {
      ...form,
      mrp: Number(form.mrp), offer_price: Number(form.offer_price), bv: Number(form.bv),
      cashback: Number(form.cashback), weight_kg: Number(form.weight_kg), stock_count: Number(form.stock_count),
      sizes: typeof form.sizes === 'string' ? form.sizes.split(',').map(s => s.trim()).filter(Boolean) : form.sizes,
      colours: typeof form.colours === 'string' ? form.colours.split(',').map(s => s.trim()).filter(Boolean) : form.colours,
    };
    try {
      if (editing === 'new') await api.post('/admin/store/products', payload);
      else await api.put(`/admin/store/products/${editing}`, payload);
      toast.success('Product saved'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/admin/store/products/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const loadSamples = async () => {
    try { const res = await api.post('/admin/store/seed-demo'); toast.success(`Added ${res.data.created} sample product(s)`); load(); }
    catch { toast.error('Could not load samples'); }
  };

  const deleteAll = async () => {
    if (!window.confirm('Remove ALL products from the store? This cannot be undone.')) return;
    try { const res = await api.delete('/admin/store/products'); toast.success(`Removed ${res.data.deleted} product(s)`); load(); }
    catch { toast.error('Delete all failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-store-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-bold display text-lg">Store products ({rows.length})</div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={loadSamples} className="gap-1" data-testid="load-samples-btn">Load samples</Button>
          {rows.length > 0 && <Button variant="outline" onClick={deleteAll} className="gap-1 border-red-400 text-red-600" data-testid="delete-all-products-btn"><Trash2 className="h-4 w-4" />Delete all</Button>}
          <Button onClick={openNew} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-1" data-testid="add-product-btn"><Pencil className="h-4 w-4" />Add product</Button>
        </div>
      </div>
      {loading && <Card className="p-6 text-center text-[var(--bz-muted)]">Loading…</Card>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(p => (
          <Card key={p.product_id} className="p-3 flex gap-3" data-testid={`admin-product-${p.product_id}`}>
            <img src={p.images?.[0] || 'https://placehold.co/80'} alt="" className="h-16 w-16 rounded object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{p.title}</div>
              <div className="text-xs text-[var(--bz-muted)]">₹{p.offer_price} · {p.bv}BV · CB ₹{p.cashback} · stock {p.stock_count}</div>
              <div className="flex gap-2 mt-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`}>Edit</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => del(p.product_id)} data-testid={`del-product-${p.product_id}`}>Delete</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-form">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Add product' : 'Edit product'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block text-sm font-semibold">Product title <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. Herbal Anti-Hairfall Shampoo" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="pf-title" />
            </div>
            <div>
              <Label className="mb-1 block text-sm font-semibold">Description</Label>
              <Textarea rows={3} placeholder="Full product description customers will see" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="pf-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-sm font-semibold">Category</Label>
                <Input placeholder="e.g. Personal Care" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} data-testid="pf-category" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">HSN code</Label>
                <Input placeholder="e.g. 3305" value={form.hsn_code} onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value }))} data-testid="pf-hsn" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">MRP (₹)</Label>
                <Input type="number" placeholder="Original price" value={form.mrp} onChange={e => setForm(f => ({ ...f, mrp: e.target.value }))} data-testid="pf-mrp" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">Offer price (₹) <span className="text-red-500">*</span></Label>
                <Input type="number" placeholder="Selling price" value={form.offer_price} onChange={e => setForm(f => ({ ...f, offer_price: e.target.value }))} data-testid="pf-offer" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">BV (Business Volume)</Label>
                <Input type="number" placeholder="BV points" value={form.bv} onChange={e => setForm(f => ({ ...f, bv: e.target.value }))} data-testid="pf-bv" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">Cashback (₹)</Label>
                <Input type="number" placeholder="Seller cashback" value={form.cashback} onChange={e => setForm(f => ({ ...f, cashback: e.target.value }))} data-testid="pf-cashback" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">Weight (kg)</Label>
                <Input type="number" step="0.1" placeholder="Used for delivery charge" value={form.weight_kg} onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))} data-testid="pf-weight" />
              </div>
              <div>
                <Label className="mb-1 block text-sm font-semibold">Stock count</Label>
                <Input type="number" placeholder="Units in stock" value={form.stock_count} onChange={e => setForm(f => ({ ...f, stock_count: e.target.value }))} data-testid="pf-stock" />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-sm font-semibold">Sizes</Label>
              <Input placeholder="Comma separated, e.g. S, M, L, XL" value={Array.isArray(form.sizes) ? form.sizes.join(', ') : form.sizes} onChange={e => setForm(f => ({ ...f, sizes: e.target.value }))} data-testid="pf-sizes" />
            </div>
            <div>
              <Label className="mb-1 block text-sm font-semibold">Colours</Label>
              <Input placeholder="Comma separated, e.g. Red, Blue, Black" value={Array.isArray(form.colours) ? form.colours.join(', ') : form.colours} onChange={e => setForm(f => ({ ...f, colours: e.target.value }))} data-testid="pf-colours" />
            </div>
            <div className="rounded-lg bg-[#EAF1FF] p-2.5 text-xs text-[#1E3A8A]">
              <b>Delivery charge</b> is auto-applied at checkout — 1 product = ₹60, 2 products = ₹90, and 3+ products are charged by total cart weight. Edit these amounts and weight slabs in the <b>Pricing</b> tab. The product weight above drives the weight-based charge.
            </div>
            <div>
              <Label className="mb-1 block text-sm font-semibold">Product images (add as many as you like)</Label>
              <label className="inline-flex items-center gap-2 mb-2 cursor-pointer text-sm font-semibold text-[#1D4ED8] bg-[var(--bz-green-tint)] rounded-full px-3 py-1.5 hover:bg-[#dbe7ff]">
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadMultiImages} data-testid="pf-multi-upload" disabled={imgBusy} />
                {imgBusy ? 'Uploading…' : '＋ Upload multiple photos at once'}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {(form.images || []).map((url, idx) => (
                  <UploadField key={`img-${idx}`} value={url} onChange={(u) => { if (!u) removeImg(idx); }} testId={`pf-img-${idx}`} />
                ))}
                <UploadField key={`img-add-${(form.images || []).length}`} value={null} onChange={(u) => { if (u) addImg(u); }} testId="pf-img-add" />
              </div>
              <div className="text-xs text-[var(--bz-muted)] mt-1">First image is the main thumbnail · {(form.images || []).length} image(s) added</div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="pf-active" /> Active (visible in store)</label>
            <Button onClick={save} disabled={busy} className="w-full bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="pf-save">{busy ? 'Saving…' : 'Save product'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const EKYC_TABS = [
  { key: 'submitted', label: 'Pending review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const AdminEkyc = ({ onDecision }) => {
  const [status, setStatus] = useState('submitted');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = async (st) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/ekyc?status=${st}`);
      setRows(res.data || []);
    } catch (err) { toast.error('Could not load eKYC list'); console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(status); }, [status]);

  const decide = async (userId, approve, note = '') => {
    try {
      const fd = new FormData();
      fd.append('user_id', userId);
      fd.append('approve', approve ? 'true' : 'false');
      fd.append('note', note);
      await api.post('/admin/ekyc/verify', fd);
      toast.success(approve ? 'eKYC approved — 5% TDS applies' : 'eKYC rejected');
      setRejectFor(null); setRejectNote('');
      load(status);
      onDecision?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-ekyc-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {EKYC_TABS.map(t => (
          <button key={t.key} onClick={() => setStatus(t.key)} data-testid={`ekyc-filter-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${status === t.key ? 'border-[#1D4ED8] bg-[var(--bz-green-tint)] text-[#1E3A8A]' : 'border-gray-200 hover:border-[#1D4ED8]/50'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {loading && <Card className="p-6 text-center text-[var(--bz-muted)]">Loading…</Card>}
      {!loading && rows.length === 0 && (
        <Card className="p-8 text-center text-[var(--bz-muted)]" data-testid="ekyc-empty">No {status} eKYC submissions.</Card>
      )}
      {!loading && rows.length > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map(r => (
            <Card key={r.user_id} className="p-4 space-y-3" data-testid={`ekyc-card-${r.user_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bold display">{r.full_name || '—'}</div>
                  <div className="text-xs text-[var(--bz-muted)]">{r.personal_id || 'no ID'} · {r.user_phone || r.email || ''}</div>
                </div>
                <Badge className={r.status === 'approved' ? 'bg-blue-600' : r.status === 'rejected' ? 'bg-red-600' : 'bg-amber-500'}>{r.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[var(--bz-muted)]">PAN</span><div className="font-mono-bz font-bold">{r.pan_number || '—'}</div></div>
                <div><span className="text-[var(--bz-muted)]">Aadhaar</span><div className="font-mono-bz font-bold">{r.aadhar_number || '—'}</div></div>
                <div><span className="text-[var(--bz-muted)]">Bank A/C</span><div className="font-mono-bz font-bold">{r.bank_account || '—'}</div></div>
                <div><span className="text-[var(--bz-muted)]">IFSC</span><div className="font-mono-bz font-bold">{r.ifsc || '—'}</div></div>
              </div>
              <div className="rounded-md bg-[var(--bz-soft)] p-2 text-xs" data-testid={`ekyc-address-${r.user_id}`}>
                <div className="flex items-center gap-1 text-[var(--bz-muted)] font-semibold mb-0.5"><Truck className="h-3.5 w-3.5" />Delivery address</div>
                {(r.address || r.city || r.state || r.pincode) ? (
                  <div className="text-[#1E3A8A] font-medium">
                    {r.address || ''}{r.city ? `, ${r.city}` : ''}{r.state ? `, ${r.state}` : ''}
                    {r.pincode ? <span className="font-mono-bz font-bold"> — {r.pincode}</span> : ''}
                    {r.phone ? <span className="block text-[var(--bz-muted)]">📞 {r.phone}</span> : ''}
                  </div>
                ) : <div className="text-[var(--bz-muted)] italic">No delivery address on file (older signup)</div>}
              </div>
              <div className="flex gap-2">
                {[['PAN', r.pan_image_url], ['Aadhaar front', r.aadhar_image_url], ['Aadhaar back', r.aadhar_back_image_url], ['Bank', r.bankbook_image_url]].map(([lbl, url]) => url ? (
                  <ImageZoom key={lbl} src={url} alt={lbl} testId={`ekyc-img-${lbl}`}>
                    <img src={url} alt={lbl} className="h-16 w-16 rounded border object-cover" />
                  </ImageZoom>
                ) : null)}
              </div>
              {r.admin_note && <div className="text-[11px] text-[var(--bz-muted)]">Note: {r.admin_note}</div>}
              {status !== 'approved' && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={() => decide(r.user_id, true)} className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid={`ekyc-approve-${r.user_id}`}>
                    <CheckCircle2 className="h-4 w-4" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setRejectFor(r.user_id); setRejectNote(''); }} className="border-red-400 text-red-700 gap-1" data-testid={`ekyc-reject-${r.user_id}`}>
                    <XCircle className="h-4 w-4" /> Reject
                  </Button>
                </div>
              )}
              {rejectFor === r.user_id && (
                <div className="space-y-2 pt-1">
                  <Input value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="Reason (sent to user)" data-testid={`ekyc-reject-note-${r.user_id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={() => decide(r.user_id, false, rejectNote)} data-testid={`ekyc-reject-confirm-${r.user_id}`}>Confirm reject</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRejectFor(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


const PRICING_FIELDS = [
  { group: 'Entry Kit (network entry)', keys: ['entry_kit_name', 'entry_kit_cost', 'entry_kit_gst_pct', 'entry_kit_delivery', 'entry_kit_bv', 'entry_kit_validity_days', 'referral_income_entry_kit'] },
  { group: 'Website Purchase — Yearly', keys: ['website_yearly_fee', 'website_yearly_gst_pct', 'website_yearly_bv', 'website_yearly_validity_days'] },
  { group: 'Website Purchase — Monthly', keys: ['website_monthly_fee', 'website_monthly_gst_pct', 'website_monthly_bv', 'website_monthly_validity_days'] },
  { group: 'Company Store — GST & Delivery', keys: ['store_gst_pct', 'store_delivery_1_item', 'store_delivery_2_item', 'store_delivery_network_1_item', 'store_delivery_network_2_item'] },
  { group: 'Weekly PV Reward', keys: ['weekly_reward_threshold_pv', 'weekly_reward_pv', 'weekly_reward_price', 'weekly_reward_gst_pct'] },
  { group: 'IR Rank Rule', keys: ['ir_bv_threshold'] },
  { group: 'Sponsor Reward (IR within first business weeks)', keys: ['sponsor_reward_window_weeks', 'sponsor_reward_bv', 'sponsor_reward_price', 'sponsor_reward_gst_pct', 'sponsor_reward_delivery_charge', 'sponsor_reward_validity_days'] },
  { group: 'Network Rules', keys: ['referral_income', 'referral_income_90d', 'referral_income_yearly', 'referral_income_monthly', 'match_income_per_pair', 'tdsr_min_pct', 'weekly_withdrawal_cap', 'monthly_volume_cap'] },
  { group: 'Taxes — TDS', keys: ['tds_pct', 'tds_pct_no_pan'] },
];

const PricingPanel = ({ settings, updateSetting }) => {
  const [kitBusy, setKitBusy] = useState(false);
  const [fixBusy, setFixBusy] = useState(false);
  const [rpBusy, setRpBusy] = useState(false);
  const runBackfillRetail = async () => {
    setRpBusy(true);
    try {
      const prev = await api.post('/admin/backfill-retail-profit', { apply: false });
      const d = prev.data;
      if (!d.orders_credited) { toast('No missed retail profit found — everyone is already credited.'); setRpBusy(false); return; }
      const ok = window.confirm(
        `Credit missed retail profit (product cashback) on self-purchases?\n\n` +
        `Orders to credit: ${d.orders_credited}\n` +
        `Total retail profit: ₹${d.total_retail_profit.toLocaleString('en-IN')}\n\n` +
        `This adds the cashback to each buyer's income (released weekly, withdrawable after eKYC). Proceed?`
      );
      if (!ok) { setRpBusy(false); return; }
      const res = await api.post('/admin/backfill-retail-profit', { apply: true });
      toast.success(`Credited ₹${res.data.total_retail_profit.toLocaleString('en-IN')} retail profit across ${res.data.orders_credited} orders`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not backfill retail profit');
    } finally { setRpBusy(false); }
  };
  const runFixMatching = async () => {
    setFixBusy(true);
    try {
      const preview = await api.post('/admin/fix-matching-income', { apply: false });
      const d = preview.data;
      const ok = window.confirm(
        `Recalculate matching income at ₹${d.rate} per matched BV?\n\n` +
        `Unpaid match entries: ${d.unpaid_match_entries}\n` +
        `Total now: ₹${d.unpaid_total_before.toLocaleString('en-IN')}\n` +
        `Total after fix: ₹${d.unpaid_total_after.toLocaleString('en-IN')}\n\n` +
        `Forfeited entries to RESTORE to wallet: ${d.restored_forfeited_entries} (net ₹${d.restored_net_to_wallet.toLocaleString('en-IN')})\n` +
        `Already-paid entries (untouched): ${d.paid_match_entries_untouched} = ₹${d.paid_match_total.toLocaleString('en-IN')}\n\n` +
        `Proceed? This recomputes unpaid entries, restores wrongly-forfeited matching income to the wallet, and sets the rate to ₹${d.rate}.`
      );
      if (!ok) { setFixBusy(false); return; }
      const res = await api.post('/admin/fix-matching-income', { apply: true });
      const r = res.data;
      toast.success(`Done — ${r.unpaid_match_entries} recomputed, ${r.restored_forfeited_entries} restored (₹${r.restored_net_to_wallet.toLocaleString('en-IN')} net) at ₹${r.rate}/BV`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not recalculate matching income');
    } finally { setFixBusy(false); }
  };
  const kitImages = settings.entry_kit_images || (settings.entry_kit_image_url ? [settings.entry_kit_image_url] : []);
  const uploadKitImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setKitBusy(true);
    try {
      const { uploadImage } = await import('@/lib/upload');
      const { compressImage } = await import('@/lib/compress');
      const compressed = await Promise.all(files.map(f => compressImage(f, { targetKB: 70 })));
      const results = await Promise.all(compressed.map(f => uploadImage(f, { targetKb: 70 })));
      const urls = results.filter(Boolean).map(r => r.url);
      if (urls.length) {
        const next = [...kitImages, ...urls];
        updateSetting('entry_kit_images', next);
        updateSetting('entry_kit_image_url', next[0]);
        toast.success(`${urls.length} kit image${urls.length === 1 ? '' : 's'} added`);
      }
    } catch { toast.error('Upload failed'); }
    finally { setKitBusy(false); e.target.value = ''; }
  };
  const removeKitImage = (idx) => {
    const next = kitImages.filter((_, i) => i !== idx);
    updateSetting('entry_kit_images', next);
    updateSetting('entry_kit_image_url', next[0] || '');
  };
  const total = (fee, gst) => {
    const f = Number(fee || 0); const g = Number(gst || 0);
    return (f + (f * g) / 100).toFixed(2);
  };
  return (
    <div className="space-y-4" data-testid="admin-pricing-panel">
      {PRICING_FIELDS.map(({ group, keys }) => (
        <Card key={group} className="p-4">
          <div className="font-bold display mb-3 flex items-center justify-between flex-wrap gap-2">
            <span>{group}</span>
            {group.startsWith('Website') && (
              <Badge className="bg-[#1D4ED8]">Customer pays ₹{total(settings[keys[0]], settings[keys[1]])}</Badge>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {keys.map(k => {
              const isTextKey = ['entry_kit_name', 'entry_kit_description', 'entry_kit_image_url'].includes(k);
              return (
              <div key={k}>
                <Label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)]">{k.replaceAll('_', ' ').replace('pct', '(%)').replace('bv', 'BV').replace('validity days', 'validity (days)')}</Label>
                <Input defaultValue={settings[k] ?? ''} onBlur={(e) => {
                  const raw = e.target.value;
                  if (isTextKey) { updateSetting(k, raw); return; }
                  // Numeric field: strip stray characters (e.g. "18%") so we never store NaN.
                  const cleaned = String(raw).replace(/[^0-9.\-]/g, '');
                  const n = Number(cleaned);
                  updateSetting(k, Number.isFinite(n) && cleaned !== '' ? n : 0);
                }} data-testid={`pricing-${k}`} />
              </div>
              );
            })}
          </div>
          {group === 'Entry Kit (network entry)' && (
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] mb-1 block">Entry Kit images — show what's inside the kit (upload multiple)</Label>
              <label className="inline-flex items-center gap-2 mb-2 cursor-pointer text-sm font-semibold text-[#1D4ED8] bg-[var(--bz-green-tint)] rounded-full px-3 py-1.5 hover:bg-[#dbe7ff]">
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadKitImages} data-testid="entry-kit-multi-upload" disabled={kitBusy} />
                {kitBusy ? 'Uploading…' : '＋ Upload kit photos'}
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {kitImages.map((url, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border" data-testid={`entry-kit-img-${idx}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeKitImage(idx)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full h-5 w-5 grid place-items-center text-xs" data-testid={`entry-kit-img-remove-${idx}`}>×</button>
                    {idx === 0 && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center py-0.5">Main</span>}
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-[var(--bz-muted)] mt-1">First image is the main thumbnail · shown as a gallery on the join screen · {kitImages.length} image(s)</div>
            </div>
          )}
          {group === 'Network Rules' && (
            <div className="mt-2 text-[11px] text-[var(--bz-muted)]">
              <b>Matching (pair) income</b> pays <b>₹{settings.match_income_per_pair ?? 10}</b> per matched BV — for every 1 BV matched on the left with 1 BV on the right. Example: 25 BV left + 25 BV right → ₹{(Number(settings.match_income_per_pair) || 10) * 25}; 100 BV left + 100 BV right → ₹{(Number(settings.match_income_per_pair) || 10) * 100}.
              <div className="mt-2">
                <Button onClick={runFixMatching} disabled={fixBusy} className="h-8 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-xs" data-testid="fix-matching-income-btn">
                  {fixBusy ? 'Working…' : `Recalculate pending matching income at ₹${settings.match_income_per_pair ?? 10}/BV`}
                </Button>
                <div className="mt-1 text-[10px]">Recomputes every UNPAID matching entry to pairs × current rate (fixes income created at an old/wrong rate). Already-paid income is never touched. Run a preview first — it asks before applying.</div>
                <div className="mt-3">
                  <Button onClick={runBackfillRetail} disabled={rpBusy} className="h-8 rounded-full bg-[#1D4ED8] hover:bg-[#1E3A8A] text-white text-xs" data-testid="backfill-retail-profit-btn">
                    {rpBusy ? 'Working…' : 'Credit missed retail profit (self-purchase cashback)'}
                  </Button>
                  <div className="mt-1 text-[10px]">Credits product cashback (retail profit) to buyers who missed it on past self-purchases. Released weekly, withdrawable after eKYC. Idempotent — safe to re-run.</div>
                </div>
              </div>
            </div>
          )}
          {group === 'Weekly PV Reward' && (
            <div className="mt-2 text-[11px] text-[var(--bz-muted)]">
              For every <b>{settings.weekly_reward_threshold_pv ?? 100} BV</b> a member buys in a single business week (Sat–Fri) they earn one <b>{settings.weekly_reward_pv ?? 25} BV</b> free-product reward — so {settings.weekly_reward_threshold_pv ?? 100} BV → 1 reward, {(Number(settings.weekly_reward_threshold_pv) || 100) * 2} BV → 2 rewards, and so on. Each reward is claimed for a flat <b>₹{settings.weekly_reward_price ?? 500}</b> + {settings.weekly_reward_gst_pct ?? 18}% GST + BV delivery, shown on the member's Business page and claimable after the {settings.reward_hold_days ?? 28}-day hold.
            </div>
          )}
          {group === 'IR Rank Rule' && (
            <div className="mt-2 text-[11px] text-[var(--bz-muted)]">
              A networker becomes an <b>IR</b> when they complete <b>{settings.ir_bv_threshold ?? 100} BV</b> within a single business week (Sat–Fri). It can be achieved in any business week (if missed one week, they can qualify later). IR is permanent once earned.
            </div>
          )}
          {group === 'Sponsor Reward (IR within first business weeks)' && (
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-xs font-semibold" data-testid="sponsor-reward-enabled-toggle">
                <input type="checkbox" checked={settings.sponsor_reward_enabled !== false} onChange={(e) => updateSetting('sponsor_reward_enabled', e.target.checked)} />
                Sponsor reward enabled
              </label>
              <div className="text-[11px] text-[var(--bz-muted)]">
                When a newly enrolled person becomes an <b>IR</b> ({settings.ir_bv_threshold ?? 100} BV in a business week) within the first <b>{settings.sponsor_reward_window_weeks ?? 2} business week(s)</b> of their enrollment, their sponsor earns a reward to claim <b>{settings.sponsor_reward_bv ?? 25} BV</b> of products for a flat <b>₹{settings.sponsor_reward_price ?? 500}</b> + {settings.sponsor_reward_gst_pct ?? 18}% GST + ₹{settings.sponsor_reward_delivery_charge ?? 100} delivery. One reward per referred person, claimable on the Business page after the {settings.reward_hold_days ?? 28}-day hold. Validity: {Number(settings.sponsor_reward_validity_days) > 0 ? `${settings.sponsor_reward_validity_days} days` : 'no expiry'}.
              </div>
            </div>
          )}
          {group === 'Company Store — GST & Delivery' && (
            <div className="mt-2 text-[11px] text-[var(--bz-muted)]">
              GST is a single global rate — enter <b>5</b> or <b>18</b>. Network/user/store-link buyers use the <b>network</b> delivery rates; outside buyers use the standard rates. 3+ items are charged by weight slabs.
            </div>
          )}
        </Card>
      ))}
      <Card className="p-4" data-testid="admin-categories-card">
        <div className="font-bold display mb-2">Deal categories</div>
        <div className="text-xs text-[var(--bz-muted)] mb-3">One per line. Customers and dealers will see this list in the category dropdown on /deals. Keep {'\u201C'}Other{'\u201D'} at the end so dealers can type a custom name.</div>
        <Textarea
          rows={10}
          defaultValue={(settings.deal_categories || []).join('\n')}
          onBlur={(e) => {
            const list = e.target.value.split('\n').map(s => s.trim()).filter(Boolean);
            updateSetting('deal_categories', list);
          }}
          data-testid="pricing-deal_categories"
        />
      </Card>
      <Card className="p-4 border-amber-300 bg-amber-50" data-testid="admin-repair-card">
        <div className="font-bold display mb-2 text-amber-900">Data repair tools</div>
        <p className="text-xs text-amber-800 mb-3">
          One-shot maintenance for legacy data. All endpoints are <b>idempotent</b> — re-running
          is safe and does nothing once the data is already correct.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            onClick={async () => {
              if (!window.confirm('Run orphan-node repair? This will link any unlinked nodes under their sponsor + credit retroactive referral income + propagate BV.')) return;
              try {
                const { data } = await api.post('/admin/repair/orphaned-nodes');
                toast.success(`Repair done — ${data.linked} linked · ${data.referrals_credited} referrals credited · ${data.bv_propagated} BV chains propagated`);
              } catch (e) { toast.error(`Repair failed: ${e.response?.data?.detail || e.message}`); }
            }}
            data-testid="repair-orphans-btn"
            className="text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md"
          >
            Repair orphaned IDs (genealogy + referral + BV)
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Normalize all phone numbers? Cleans Devanagari numerals, zero-width spaces, +91 prefix, etc.')) return;
              try {
                const { data } = await api.post('/admin/migrate/normalize-phones');
                toast.success(`Phones normalized — ${data.users_fixed} user(s), ${data.deals_fixed} deal(s) cleaned`);
              } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
            }}
            data-testid="normalize-phones-btn"
            className="text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md"
          >
            Normalize all phones
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Migrate personal IDs from BT* to IL10001+ series? Renames admin to INDIVALIFE.')) return;
              try {
                const { data } = await api.post('/admin/migrate/personal-ids');
                const mapping = data.mapping || {};
                const count = Object.keys(mapping).length;
                toast.success(`Migrated ${count} ID(s). ${data.fk_updates || 0} FK refs updated.`);
              } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
            }}
            data-testid="migrate-ids-btn"
            className="text-sm font-bold bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-md"
          >
            Migrate personal IDs (BT* → IL10001+)
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('De-duplicate personal IDs? Keeps the OLDEST node for each duplicated ID (e.g. sponsor stays IL10001) and renumbers the rest sequentially (IL10002, IL10003, …). Updates all FK refs.')) return;
              try {
                const { data } = await api.post('/admin/repair/dedupe-personal-ids');
                toast.success(`Dedupe done — ${data.duplicate_groups} dup group(s), ${data.renumbered_count} renumbered, ${data.parent_links_updated} parent links fixed.`);
              } catch (e) { toast.error(`Dedupe failed: ${e.response?.data?.detail || e.message}`); }
            }}
            data-testid="dedupe-ids-btn"
            className="text-sm font-bold bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md"
          >
            De-duplicate personal IDs (keep oldest, renumber rest)
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Recompute ALL volume (BV) from scratch using the corrected leg logic? Resets every left_bv/right_bv to 0, drops UNPAID match income, then replays every signup chronologically. Already-paid match income is preserved.')) return;
              try {
                const { data } = await api.post('/admin/repair/recompute-bv');
                toast.success(`BV recomputed — ${data.nodes_reset} node(s) reset, ${data.signups_replayed} signup(s) replayed, ${data.match_entries_dropped} pending match entries dropped.`);
              } catch (e) { toast.error(`Recompute failed: ${e.response?.data?.detail || e.message}`); }
            }}
            data-testid="recompute-bv-btn"
            className="text-sm font-bold bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md"
          >
            Recompute all volume (BV) — fix inverted legs
          </button>
          <button
            onClick={async () => {
              const c1 = window.prompt('⚠️ FACTORY RESET — This will permanently delete EVERY user, member, signup, payment, deal, chat, tambola room, audit log, etc.  ONLY the admin account and INDIVALIFE company-top node are kept.  Type FACTORY-RESET to confirm.');
              if (c1 !== 'FACTORY-RESET') { toast('Cancelled — phrase did not match.'); return; }
              if (!window.confirm('Are you 100% sure? This is irreversible.')) return;
              try {
                const { data } = await api.post('/admin/factory-reset');
                toast.success(`Factory reset complete — ${data.total_docs_deleted} docs deleted across ${Object.keys(data.per_collection || {}).length} collections. Application is now FRESH.`);
                setTimeout(() => window.location.reload(), 2500);
              } catch (e) { toast.error(`Reset failed: ${e.response?.data?.detail || e.message}`); }
            }}
            data-testid="factory-reset-btn"
            className="text-sm font-bold bg-black hover:bg-red-900 text-white px-3 py-2 rounded-md col-span-full ring-2 ring-red-500"
          >
            ☠️ FACTORY RESET — delete all data (keep admin + settings)
          </button>
        </div>
      </Card>

      <p className="text-xs text-[var(--bz-muted)]">Changes apply to <b>new</b> Razorpay orders immediately. Existing subscriptions keep their original validity. To roll out a new price publicly, you may also want to update the landing-page copy.</p>
    </div>
  );
};

const AnnouncementsPanel = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', body: '', kind: 'update', pinned: false });
  const load = async () => {
    try { const { data } = await api.get('/admin/announcements'); setItems(data); }
    catch (e) { toast.error(`Load failed: ${e.response?.data?.detail || e.message}`); }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title.trim() && !form.body.trim()) { toast.error('Add a title or body'); return; }
    try {
      await api.post('/admin/announcements', form);
      toast.success('Announcement posted');
      setForm({ title: '', body: '', kind: 'update', pinned: false });
      load();
    } catch (e) { toast.error(`Failed: ${e.response?.data?.detail || e.message}`); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    await api.delete(`/admin/announcements/${id}`);
    load();
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-announcements-panel">
      <Card className="p-4">
        <div className="font-bold display mb-3">Post an update</div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g., Diwali trip to Manali — qualify by 31 Oct" data-testid="ann-title" />
          </div>
          <div>
            <Label className="text-xs">Body</Label>
            <Textarea rows={4} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Tell members what's new, what to do, deadlines, prizes…" data-testid="ann-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Kind</Label>
              <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className="w-full h-10 rounded-md border px-3 text-sm" data-testid="ann-kind">
                <option value="update">📣 Update</option>
                <option value="trip">✈️ Trip / Travel</option>
                <option value="prize">🏆 Prize / Contest</option>
                <option value="winner">🥇 Winner announcement</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
              <input type="checkbox" checked={form.pinned} onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))} className="h-4 w-4 accent-[#1D4ED8]" data-testid="ann-pin" />
              <span>Pin to top</span>
            </label>
          </div>
          <Button onClick={create} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] w-full" data-testid="ann-create-btn">Post announcement</Button>
        </div>
      </Card>
      <Card className="p-4">
        <div className="font-bold display mb-3">Live ({items.length})</div>
        <div className="space-y-2 max-h-[480px] overflow-auto bz-scroll">
          {items.length === 0 && <div className="text-xs text-[var(--bz-muted)] text-center py-6">No announcements yet.</div>}
          {items.map(a => (
            <div key={a.announcement_id} className="border rounded-lg p-3" data-testid={`ann-row-${a.announcement_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{a.kind}</Badge>
                    {a.pinned && <Badge className="bg-amber-500 text-[10px]">📌 Pinned</Badge>}
                  </div>
                  <div className="font-bold text-sm mt-1">{a.title}</div>
                  <div className="text-xs text-[var(--bz-muted)] mt-0.5 whitespace-pre-wrap">{a.body}</div>
                  <div className="text-[10px] text-[var(--bz-muted)] mt-1">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(a.announcement_id)} data-testid={`ann-del-${a.announcement_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const MessagesPanel = () => {
  const [form, setForm] = useState({ to: 'all', body: '' });
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);
  const send = async () => {
    if (!form.body.trim()) { toast.error('Type a message'); return; }
    setSending(true);
    try {
      const { data } = await api.post('/admin/messages/dm', form);
      toast.success(`Sent to ${data.sent} user${data.sent === 1 ? '' : 's'}`);
      setForm(f => ({ ...f, body: '' }));
    } catch (e) { toast.error(`Send failed: ${e.response?.data?.detail || e.message}`); }
    finally { setSending(false); }
  };
  return (
    <Card className="p-4 max-w-2xl" data-testid="admin-messages-panel">
      <div className="font-bold display mb-3">Send a message</div>
      <p className="text-xs text-[var(--bz-muted)] mb-3">Lands in the user's INDIVALIFE Chats inbox instantly. Pick a specific person or broadcast to everyone.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Recipient</Label>
          <select value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} className="w-full h-10 rounded-md border px-3 text-sm" data-testid="dm-to">
            <option value="all">📢 Broadcast to ALL active members ({users.length})</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>👤 {u.name || u.email || u.personal_id} {u.personal_id ? `(${u.personal_id})` : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Message</Label>
          <Textarea rows={5} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Hi there! Here's an important update from INDIVALIFE…" data-testid="dm-body" />
        </div>
        <Button onClick={send} disabled={sending} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] w-full" data-testid="dm-send-btn">{sending ? 'Sending…' : (form.to === 'all' ? `Broadcast to ${users.length} member${users.length === 1 ? '' : 's'}` : 'Send DM')}</Button>
      </div>
    </Card>
  );
};

const PrizesPanel = () => {
  const [prizes, setPrizes] = useState([]);
  const [form, setForm] = useState({
    name: '', description: '', image_url: '', metric: 'volume',
    threshold: 100, period_days: 30, prize_value: '', max_winners: 10, active: true,
  });
  const [busyId, setBusyId] = useState(null);
  const [qualifiers, setQualifiers] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get('/admin/prizes');
      setPrizes(data || []);
    } catch (e) { toast.error('Failed to load prizes'); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try {
      await api.post('/admin/prizes', { ...form, threshold: Number(form.threshold), period_days: Number(form.period_days), max_winners: Number(form.max_winners) || null });
      toast.success('Prize criteria created');
      setForm({ name: '', description: '', image_url: '', metric: 'volume', threshold: 100, period_days: 30, prize_value: '', max_winners: 10, active: true });
      load();
    } catch (e) { toast.error(`Create failed: ${e.response?.data?.detail || e.message}`); }
  };

  const computeQualifiers = async (pid) => {
    setBusyId(pid);
    try {
      const { data } = await api.get(`/admin/prizes/${pid}/qualifiers`);
      setQualifiers(q => ({ ...q, [pid]: data.qualifiers }));
      toast.success(`${data.count} qualifying member(s)`);
    } catch (e) { toast.error('Failed to compute qualifiers'); }
    finally { setBusyId(null); }
  };

  const announce = async (pid) => {
    if (!window.confirm('Announce winners? This will DM each qualifier and post a global announcement.')) return;
    setBusyId(pid);
    try {
      const { data } = await api.post(`/admin/prizes/${pid}/announce`);
      toast.success(`Announced to ${data.count} winner(s)`);
      load();
    } catch (e) { toast.error(`Announce failed: ${e.response?.data?.detail || e.message}`); }
    finally { setBusyId(null); }
  };

  const remove = async (pid) => {
    if (!window.confirm('Delete this prize criteria?')) return;
    try { await api.delete(`/admin/prizes/${pid}`); load(); } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-prizes-panel">
      <Card className="p-4">
        <div className="font-bold display mb-3">Create prize criteria</div>
        <div className="space-y-2">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Singapore Trip — March" data-testid="prize-name" /></div>
          <div><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="prize-desc" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Metric</Label>
              <select value={form.metric} onChange={e => setForm(f => ({ ...f, metric: e.target.value }))} className="w-full h-10 rounded-md border px-3 text-sm" data-testid="prize-metric">
                <option value="volume">Volume (₹ match income)</option>
                <option value="referrals">Referrals (count)</option>
                <option value="matched_pairs">Matched pairs (BV)</option>
              </select>
            </div>
            <div><Label className="text-xs">Threshold</Label><Input type="number" value={form.threshold} onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))} data-testid="prize-threshold" /></div>
            <div><Label className="text-xs">Period (days)</Label><Input type="number" value={form.period_days} onChange={e => setForm(f => ({ ...f, period_days: e.target.value }))} data-testid="prize-period" /></div>
            <div><Label className="text-xs">Max winners</Label><Input type="number" value={form.max_winners} onChange={e => setForm(f => ({ ...f, max_winners: e.target.value }))} data-testid="prize-max" /></div>
          </div>
          <div><Label className="text-xs">Prize value</Label><Input value={form.prize_value} onChange={e => setForm(f => ({ ...f, prize_value: e.target.value }))} placeholder="Bali 4N5D Trip + ₹25,000 cash" data-testid="prize-value" /></div>
          <div><Label className="text-xs">Image URL (optional)</Label><Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} data-testid="prize-image" /></div>
          <Button onClick={create} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] w-full" data-testid="prize-create-btn">Create criteria</Button>
        </div>
      </Card>

      <Card className="p-4">
        <div className="font-bold display mb-3">Active prize criteria ({prizes.length})</div>
        <div className="space-y-3 max-h-[600px] overflow-auto">
          {prizes.length === 0 && <div className="text-sm text-[var(--bz-muted)]">No prize criteria yet.</div>}
          {prizes.map(p => (
            <Card key={p.prize_id} className="p-3 border" data-testid={`prize-card-${p.prize_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-sm">{p.name}</div>
                  <div className="text-xs text-[var(--bz-muted)]">{p.description}</div>
                  <div className="text-[10px] mt-1 font-mono-bz">
                    {p.metric} ≥ {p.threshold} · {p.period_days}d window · max {p.max_winners || '∞'} winners
                  </div>
                  {p.prize_value && <div className="text-xs mt-1">🏆 {p.prize_value}</div>}
                  {p.announced && <Badge variant="outline" className="mt-1 text-[10px]">Announced</Badge>}
                </div>
                <button onClick={() => remove(p.prize_id)} className="text-xs text-red-600 hover:underline">delete</button>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" disabled={busyId === p.prize_id} onClick={() => computeQualifiers(p.prize_id)} className="text-xs h-7" data-testid={`prize-compute-${p.prize_id}`}>Compute qualifiers</Button>
                {!p.announced && (
                  <Button size="sm" disabled={busyId === p.prize_id} onClick={() => announce(p.prize_id)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] text-xs h-7" data-testid={`prize-announce-${p.prize_id}`}>Announce winners</Button>
                )}
              </div>
              {qualifiers[p.prize_id] && (
                <div className="mt-2 text-xs">
                  <div className="font-semibold mb-1">Qualifiers ({qualifiers[p.prize_id].length}):</div>
                  <div className="max-h-32 overflow-auto space-y-0.5 font-mono-bz text-[10px]">
                    {qualifiers[p.prize_id].map(q => (
                      <div key={q.user_id}>{q.personal_id || '—'} · {q.name} · {q.phone} · score {q.score}</div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </Card>
    </div>
  );
};

